import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PACKAGE_EVENT_TYPES,
  TENANT_LOG_SOURCES,
  type SystemLogEntry,
  type TenantLogSource,
} from '@azko/shared';

import { api } from '../lib/api.js';
import { LogFeed, describeLogAction } from '../components/LogFeed.js';
import {
  Button,
  Card,
  EmptyState,
  LoadingRows,
  PageHeader,
  SearchInput,
  Select,
  cx,
} from '../components/ui.js';

/**
 * O historico do condominio.
 *
 * Duas trilhas: as acoes administrativas (morador, porteiro, unidade, plano) e
 * a vida das encomendas. A tela abre so com a primeira — que e o que ela sempre
 * mostrou — porque um predio de 400 unidades gera dezenas de eventos de
 * encomenda por dia, e ligar os dois por padrao esconderia "cortaram o acesso
 * do porteiro da noite" atras deles.
 *
 * O que mudou e que agora da para pedir a outra. Antes o sindico que quisesse
 * saber a que horas a encomenda do 101 saiu nao tinha onde olhar.
 */

interface LogsResponse {
  items: SystemLogEntry[];
  nextCursor: string | null;
  /** Acoes administrativas ja registradas neste predio. */
  actions: string[];
}

const SOURCE_LABELS: Record<TenantLogSource, string> = {
  condominio: 'Administrativo',
  encomenda: 'Encomendas',
};

const PERIODS = [
  { value: '7', label: 'Ultimos 7 dias' },
  { value: '30', label: 'Ultimos 30 dias' },
  { value: '90', label: 'Ultimos 90 dias' },
  { value: 'todos', label: 'Desde o comeco' },
];

export function LogsPage() {
  const [sources, setSources] = useState<TenantLogSource[]>(['condominio']);
  const [action, setAction] = useState('');
  const [days, setDays] = useState('30');
  const [search, setSearch] = useState('');
  // Pilha de cursores: cada "mais antigos" empilha, e trocar o filtro zera —
  // um cursor da lista anterior nao significa nada na lista filtrada.
  const [pages, setPages] = useState<string[]>([]);

  const before = pages[pages.length - 1] ?? null;
  const effectiveSearch = search.trim().length >= 2 ? search.trim() : '';

  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPages([]);
    };
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['logs', sources.join(','), action, days, effectiveSearch, before],
    queryFn: () => {
      const params = new URLSearchParams();
      if (sources.length > 0) params.set('sources', sources.join(','));
      if (action) params.set('action', action);
      if (days) params.set('days', days);
      if (effectiveSearch) params.set('search', effectiveSearch);
      if (before) params.set('before', before);
      return api.get<LogsResponse>(`/logs?${params.toString()}`);
    },
    placeholderData: (previous) => previous,
  });

  const items = data?.items ?? [];
  const showingPackages = sources.includes('encomenda');

  const actionOptions = useMemo(() => {
    const list: Array<{ value: string; label: string }> = [];

    if (sources.includes('condominio')) {
      for (const item of data?.actions ?? []) {
        list.push({ value: item, label: describeLogAction('condominio', item) });
      }
    }
    if (showingPackages) {
      for (const type of PACKAGE_EVENT_TYPES) {
        list.push({
          value: `encomenda.${type}`,
          label: describeLogAction('encomenda', `encomenda.${type}`),
        });
      }
    }

    return list;
  }, [data?.actions, showingPackages, sources]);

  function toggleSource(source: TenantLogSource) {
    setPages([]);
    setAction('');
    setSources((current) => {
      const next = current.includes(source)
        ? current.filter((item) => item !== source)
        : [...current, source];
      // Desmarcar as duas devolveria uma lista vazia sem explicacao. A ultima
      // ligada nao desliga.
      return next.length > 0 ? next : current;
    });
  }

  return (
    <>
      <PageHeader title="Historico" subtitle="O que aconteceu no predio — e quem fez." />

      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap gap-2">
          {TENANT_LOG_SOURCES.map((source) => {
            const on = sources.includes(source);
            return (
              <button
                key={source}
                type="button"
                onClick={() => toggleSource(source)}
                aria-pressed={on}
                className={cx(
                  // Alvo de toque: py-2 no celular passa dos 40px de altura. O py-1.5 do
                  // desktop dava 32px — abaixo do que um polegar acerta sem tentar duas vezes.
                  'rounded-pill border px-4 py-2 text-sm font-semibold transition sm:px-3 sm:py-1.5',
                  on
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-line text-muted hover:border-line-strong hover:text-fg',
                )}
              >
                {SOURCE_LABELS[source]}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <Select
            value={action}
            onChange={reset(setAction)}
            emptyOption="Todas as acoes"
            options={actionOptions}
            aria-label="Filtrar por acao"
          />
          <Select value={days} onChange={reset(setDays)} options={PERIODS} aria-label="Periodo" />
          <SearchInput
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPages([]);
            }}
            placeholder="Quem fez"
            aria-label="Buscar por quem fez"
          />
        </div>
      </Card>

      {isLoading ? (
        <LoadingRows rows={6} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nada neste recorte"
          description={
            action || effectiveSearch
              ? 'Nenhum registro combina com o filtro. Tente ampliar o periodo.'
              : 'Nada foi registrado no periodo escolhido. Aumente a janela para ver o que veio antes.'
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <LogFeed
            entries={items}
            showSource={showingPackages && sources.length > 1}
            footer={
              <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-4 py-4 sm:px-6">
                {pages.length > 0 && (
                  <Button variant="ghost" onClick={() => setPages((p) => p.slice(0, -1))}>
                    Voltar
                  </Button>
                )}
                {data?.nextCursor && (
                  <Button
                    variant="ghost"
                    loading={isFetching}
                    onClick={() => setPages((p) => [...p, data.nextCursor as string])}
                  >
                    Mais antigos
                  </Button>
                )}
                {pages.length > 0 && (
                  <Button variant="quiet" onClick={() => setPages([])}>
                    Voltar ao inicio
                  </Button>
                )}
              </div>
            }
          />
        </Card>
      )}
    </>
  );
}
